import { useUserLocale } from 'src/user/UserContext'
import { useNotificationService } from 'src/shared/utils/NotificationService'
import {
  FeatureData,
  useFeatureConfiguration,
} from 'src/screens/shared/configuration/feature/FeaturesConfigurationUtils'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import {
  Feature_Config,
  Feature_Group_Config,
  Feature_Group_Sorting_Config,
  Feature_Sorting_Config,
  Feature_Sorting_Config_Bool_Exp,
  Feature_Type_Config,
  Feature_Type_Sorting_Config,
} from 'src/@types/graphql'
import { Box, IconButton, Typography } from '@mui/material'
import { EditIcon, MoveDownIcon, MoveUpIcon } from 'src/shared/icons/Icons'
import { S } from 'src/shared/styled/S'
import { LEVEL_TYPE, PROCESS, PROCESS_TYPE, PROJECT_TYPE } from 'src/shared/constants/constants'
import { useMessageSource } from 'src/i18n/useMessageSource'
import { debounce } from 'lodash'
import { ROUTES } from 'src/routing/routes'
import { useDelayedNavigate } from 'src/shared/utils/hooks/navigation-hooks'

type Props = {
  projectType?: PROJECT_TYPE
  level?: LEVEL_TYPE
}

export const FeaturesSortList = ({ projectType, level }: Props) => {
  const language = useUserLocale()
  const notificationService = useNotificationService()
  const navigate = useDelayedNavigate()
  const { getMessage } = useMessageSource()
  const {
    fetchAllFeatureConfigData,
    getNestedFeatureConfigData,
    switchFeatureGroupPosition,
    switchFeaturePosition,
    switchFeatureTypePosition,
  } = useFeatureConfiguration()

  const processType: PROCESS_TYPE | undefined =
    projectType !== undefined ? projectType : level !== undefined ? PROCESS.KAP : undefined

  // state
  const [featuresData, setFeaturesData] = useState<FeatureData | undefined>()
  const [isReordering, setIsReordering] = useState(false)

  // constants
  const featureSortingConfigWhere: Feature_Sorting_Config_Bool_Exp = useMemo(() => {
    if (projectType) {
      return {
        process: {
          _eq: projectType,
        },
      }
    }

    if (level) {
      return {
        level: {
          _eq: level,
        },
      }
    }

    return {}
  }, [projectType, level])

  // fetch data
  const fetchData = useCallback(async () => {
    fetchAllFeatureConfigData(featureSortingConfigWhere)
      .then((data) => {
        setFeaturesData(data)
      })
      .catch((err) => {
        notificationService.operationFailed()
        setFeaturesData(undefined)
        console.error(err)
      })
  }, [featureSortingConfigWhere])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  const nested_feature_data = useMemo(() => {
    const nestedFeatureData = getNestedFeatureConfigData(featuresData)?.nestedFeatureData

    setIsReordering(false)

    return nestedFeatureData
  }, [featuresData])

  const feature_type_map = useMemo(() => {
    return new Map(featuresData?.featuresTypeConfigs.map((ft) => [ft.id, ft]))
  }, [featuresData])

  const feature_group_map = useMemo(() => {
    return new Map(featuresData?.featureGroupConfigs.map((fg) => [fg.id, fg]))
  }, [featuresData])

  // handlers
  const onSwitchFeatureTypePosition = useCallback(
    async (ft1?: Feature_Type_Sorting_Config, ft2?: Feature_Type_Sorting_Config) => {
      if (!ft1 || !ft2) {
        notificationService.operationFailed()
        return
      }

      try {
        await switchFeatureTypePosition(ft1, ft2, projectType, level)
        await fetchData()
      } catch (e) {
        notificationService.operationFailed()
        throw e
      }
    },
    [notificationService, switchFeatureTypePosition, projectType, level, fetchData],
  )

  const onSwitchFeatureGroupPosition = useCallback(
    async (fg1?: Feature_Group_Sorting_Config, fg2?: Feature_Group_Sorting_Config) => {
      if (!fg1 || !fg2) {
        notificationService.operationFailed()
        return
      }
      try {
        await switchFeatureGroupPosition(fg1, fg2, projectType, level)
        await fetchData()
      } catch (e) {
        notificationService.operationFailed()
        throw e
      }
    },
    [notificationService, switchFeatureGroupPosition, projectType, level, fetchData],
  )

  const onSwitchFeaturePosition = useCallback(
    async (f1?: Feature_Sorting_Config, f2?: Feature_Sorting_Config) => {
      if (!f1 || !f2) {
        notificationService.operationFailed()
        return
      }

      try {
        await switchFeaturePosition(f1, f2, projectType, level)
        await fetchData()
      } catch (e) {
        notificationService.operationFailed()
        throw e
      }
    },
    [notificationService, switchFeaturePosition, projectType, level, fetchData],
  )

  const handleOnClickFeatureTypeReorder = useCallback(
    async (featureTypes: [number, Map<number | null, Feature_Config[]>][], index: number, moveUp: boolean) => {
      if (index < 0 || index >= featureTypes.length) return

      const curr = feature_type_map.get(featureTypes[index]?.[0])?.feature_type_sorting_configs?.[0] ?? undefined

      let prev
      let next

      if (moveUp) {
        prev = feature_type_map.get(featureTypes[index - 1]?.[0])?.feature_type_sorting_configs?.[0] ?? undefined
        await onSwitchFeatureTypePosition(curr, prev)
      } else {
        next = feature_type_map.get(featureTypes[index + 1]?.[0])?.feature_type_sorting_configs?.[0] ?? undefined
        await onSwitchFeatureTypePosition(curr, next)
      }
    },
    [onSwitchFeatureTypePosition],
  )

  const handleOnClickFeatureGroupReorder = useCallback(
    async (featureGroups: [number | null, Feature_Config[]][], index: number, moveUp: boolean) => {
      if (index < 0 || index >= featureGroups.length) return

      let curr
      let prev
      let next

      const currFgId = featureGroups[index]?.[0]
      if (currFgId) {
        curr = feature_group_map.get(currFgId)?.feature_group_sorting_configs?.[0] ?? undefined
      }

      if (moveUp) {
        const prevFgId = featureGroups[index - 1]?.[0]
        if (prevFgId) {
          prev = feature_group_map.get(prevFgId)?.feature_group_sorting_configs?.[0] ?? undefined
        }

        await onSwitchFeatureGroupPosition(curr, prev)
      } else {
        const nextFgId = featureGroups[index + 1]?.[0]

        if (nextFgId) {
          next = feature_group_map.get(nextFgId)?.feature_group_sorting_configs?.[0] ?? undefined
        }
        await onSwitchFeatureGroupPosition(curr, next)
      }
    },
    [onSwitchFeatureGroupPosition],
  )

  const handleOnClickFeatureReorder = useCallback(
    async (features: Feature_Config[], index: number, moveUp: boolean) => {
      if (index < 0 || index >= features.length) return

      const curr = features[index]?.feature_sorting_configs?.[0] ?? undefined

      let prev
      let next

      if (moveUp) {
        prev = features[index - 1]?.feature_sorting_configs?.[0] ?? undefined
        await onSwitchFeaturePosition(curr, prev)
      } else {
        next = features[index + 1]?.feature_sorting_configs?.[0] ?? undefined
        await onSwitchFeaturePosition(curr, next)
      }
    },
    [onSwitchFeaturePosition],
  )

  const debouncedHandleReorder = useMemo(
    () =>
      debounce(
        async (reorderFunction: (...args: any[]) => Promise<void>, ...args: any[]) => {
          if (isReordering) return

          setIsReordering(true)

          try {
            await reorderFunction(...args)
            await new Promise((resolve) => setTimeout(resolve, 500))
          } catch {
            setIsReordering(false)
            console.error('Error caught in features sort debounce handler reorder method')
          }
        },
        500,
        { leading: true, trailing: false },
      ),
    [],
  )

  const onEditFeatureGroupClick = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>, featureGroupId: number) => {
      e.stopPropagation()
      navigate(
        ROUTES.FeaturesConfigurationIndex.nested.FeaturesConfigurationFeatureGroupsEdit.params({
          featureGroupId,
        }),
        { state: { processType: processType } },
      )
    },
    [navigate, processType],
  )

  const onEditFeatureTypeClick = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>, featureTypeId: number) => {
      e.stopPropagation()
      navigate(
        ROUTES.FeaturesConfigurationIndex.nested.FeaturesConfigurationFeatureTypesEdit.params({
          featureTypeId,
        }),
        { state: { processType: processType } },
      )
    },
    [navigate, processType],
  )

  const onEditFeatureClick = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>, featureConfigId: number) => {
      e.stopPropagation()
      navigate(
        ROUTES.FeaturesConfigurationIndex.nested.FeaturesConfigurationFeaturesEdit.params({
          featureConfigId,
        }),
        { state: { processType: processType } },
      )
    },
    [navigate, processType],
  )

  // JSX
  const renderFeatureTypeTitle = (
    featureType: Feature_Type_Config,
    isUpDisabled: boolean,
    isDownDisabled: boolean,
    featureTypes: [number, Map<number | null, Feature_Config[]>][],
    index: number,
  ) => {
    return (
      <S.ConfigurationSortCard.Card greyedOut={!featureType.active} transparent={true}>
        <Box className="header-wrapper">
          <Box className="header-content">
            <S.ConfigurationSortCard.Header
              avatar={
                <S.ConfigurationSortCard.Box>
                  <S.ConfigurationSortCard.MoveButtons>
                    <IconButton
                      color="inherit"
                      disabled={isUpDisabled || isReordering}
                      onClick={() => debouncedHandleReorder(handleOnClickFeatureTypeReorder, featureTypes, index, true)}
                      size="small"
                    >
                      <MoveUpIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      color="inherit"
                      disabled={isDownDisabled || isReordering}
                      onClick={() =>
                        debouncedHandleReorder(handleOnClickFeatureTypeReorder, featureTypes, index, false)
                      }
                      size="small"
                    >
                      <MoveDownIcon fontSize="small" />
                    </IconButton>
                  </S.ConfigurationSortCard.MoveButtons>
                </S.ConfigurationSortCard.Box>
              }
              title={
                <S.Box.TruncateBox>
                  <Typography variant="h2" color={'textPrimary'} sx={{ flexGrow: 1 }}>
                    {featureType.names[language]}
                  </Typography>
                </S.Box.TruncateBox>
              }
              action={
                <IconButton
                  sx={{ m: 0, mr: -2 }}
                  onClick={(e) => onEditFeatureTypeClick(e, featureType.id)}
                  color="primary"
                  size={'small'}
                >
                  <EditIcon fontSize={'small'} />
                </IconButton>
              }
              disableTypography
            />
          </Box>
        </Box>
      </S.ConfigurationSortCard.Card>
    )
  }

  const renderFeatureGroupTitle = (
    featureGroup: Feature_Group_Config,
    isUpDisabled: boolean,
    isDownDisabled: boolean,
    featureGroups: [number | null, Feature_Config[]][],
    index: number,
  ) => {
    return (
      <S.ConfigurationSortCard.Card greyedOut={!featureGroup.active} transparent={true}>
        <Box className="header-wrapper">
          <Box className="header-content">
            <S.ConfigurationSortCard.Header
              avatar={
                <S.ConfigurationSortCard.Box>
                  <S.ConfigurationSortCard.MoveButtons>
                    <IconButton
                      color="inherit"
                      disabled={isUpDisabled || isReordering}
                      onClick={() =>
                        debouncedHandleReorder(handleOnClickFeatureGroupReorder, featureGroups, index, true)
                      }
                      size="small"
                    >
                      <MoveUpIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      color="inherit"
                      disabled={isDownDisabled || isReordering}
                      onClick={() =>
                        debouncedHandleReorder(handleOnClickFeatureGroupReorder, featureGroups, index, false)
                      }
                      size="small"
                    >
                      <MoveDownIcon fontSize="small" />
                    </IconButton>
                  </S.ConfigurationSortCard.MoveButtons>
                </S.ConfigurationSortCard.Box>
              }
              title={
                <S.Box.TruncateBox>
                  <Typography variant="h4" color={'textPrimary'} fontWeight={300} sx={{ flexGrow: 1 }}>
                    {featureGroup.names[language]}
                  </Typography>
                </S.Box.TruncateBox>
              }
              action={
                <IconButton
                  sx={{ m: 0, mr: -2 }}
                  onClick={(e) => onEditFeatureGroupClick(e, featureGroup.id)}
                  color="primary"
                  size={'small'}
                >
                  <EditIcon fontSize={'small'} />
                </IconButton>
              }
              disableTypography
            />
          </Box>
        </Box>
      </S.ConfigurationSortCard.Card>
    )
  }

  const renderFeatureConfig = (
    featureConfig: Feature_Config,
    isUpDisabled: boolean,
    isDownDisabled: boolean,
    features: Feature_Config[],
    index: number,
  ) => {
    return (
      <Box mt={1} key={featureConfig.id}>
        <S.ConfigurationSortCard.Card greyedOut={!featureConfig.active}>
          <Box className="header-wrapper">
            <Box className="header-content">
              <S.ConfigurationSortCard.Header
                avatar={
                  <S.ConfigurationSortCard.Box>
                    <S.ConfigurationSortCard.MoveButtons>
                      <IconButton
                        color="inherit"
                        disabled={isUpDisabled || isReordering}
                        onClick={() => debouncedHandleReorder(handleOnClickFeatureReorder, features, index, true)}
                        size="large"
                      >
                        <MoveUpIcon />
                      </IconButton>
                      <IconButton
                        color="inherit"
                        disabled={isDownDisabled || isReordering}
                        onClick={() => debouncedHandleReorder(handleOnClickFeatureReorder, features, index, false)}
                        size="large"
                      >
                        <MoveDownIcon />
                      </IconButton>
                    </S.ConfigurationSortCard.MoveButtons>
                  </S.ConfigurationSortCard.Box>
                }
                title={
                  <S.Box.TruncateBox>
                    <Typography variant="subtitle2" color={'textPrimary'} sx={{ flexGrow: 1 }}>
                      <>
                        {featureConfig.names[language]}

                        {featureConfig.selection_type === 'TEXT' && (
                          <span style={{ fontStyle: 'italic', marginLeft: 5, fontSize: '0.8em' }}>
                            {getMessage('label.others.features')}
                          </span>
                        )}
                      </>
                    </Typography>
                  </S.Box.TruncateBox>
                }
                action={
                  <IconButton
                    sx={{ m: 0, mr: -2 }}
                    onClick={(e) => onEditFeatureClick(e, featureConfig.id)}
                    color="primary"
                    size={'small'}
                  >
                    <EditIcon fontSize={'small'} />
                  </IconButton>
                }
                disableTypography
              />
            </Box>
          </Box>
        </S.ConfigurationSortCard.Card>
      </Box>
    )
  }

  return (
    <>
      {featuresData &&
        nested_feature_data &&
        Array.from(nested_feature_data.entries()).map(([typeId, groups], ftIndex) => {
          const featureType = feature_type_map.get(typeId)
          const featureGroups = Array.from(groups.entries()).filter(([groupId]) => groupId !== null)
          const standAloneFeatureConfigs = groups.get(null)

          // render feature type props
          const isUpDisabled = ftIndex === 0
          const isDownDisabled = ftIndex === Array.from(nested_feature_data.entries()).length - 1

          const featureTypes = Array.from(nested_feature_data.entries())

          return (
            featureType && (
              <Box key={featureType.id}>
                {renderFeatureTypeTitle(featureType, isUpDisabled, isDownDisabled, featureTypes, ftIndex)}

                <Box pl={2}>
                  {/* First render grouped features (non-null keys) */}
                  {featureGroups.map(([groupId, features], fgIndex) => {
                    if (groupId !== null) {
                      const featureGroup = feature_group_map.get(groupId)

                      // render feature group props
                      const isUpDisabled = fgIndex === 0
                      const isDownDisabled = fgIndex === Object.entries(featureGroups).length - 1

                      return (
                        featureGroup && (
                          <Box mt={1} key={featureGroup.id}>
                            {renderFeatureGroupTitle(
                              featureGroup,
                              isUpDisabled,
                              isDownDisabled,
                              featureGroups,
                              fgIndex,
                            )}

                            <Box pl={2}>
                              {features.map((featureConfig, fcIndex) => {
                                // render feature config props
                                const isUpDisabled = fcIndex === 0
                                const isDownDisabled = fcIndex === features.length - 1

                                return renderFeatureConfig(
                                  featureConfig,
                                  isUpDisabled,
                                  isDownDisabled,
                                  features,
                                  fcIndex,
                                )
                              })}
                            </Box>
                          </Box>
                        )
                      )
                    }
                  })}
                </Box>

                {/* Last render standalone features (null group) */}
                <Box pl={2}>
                  {standAloneFeatureConfigs &&
                    standAloneFeatureConfigs.map((featureConfig, standAloneFcIndex) => {
                      // render feature config props
                      const isUpDisabled = standAloneFcIndex === 0
                      const isDownDisabled = standAloneFcIndex === standAloneFeatureConfigs.length - 1

                      return renderFeatureConfig(
                        featureConfig,
                        isUpDisabled,
                        isDownDisabled,
                        standAloneFeatureConfigs,
                        standAloneFcIndex,
                      )
                    })}
                </Box>
              </Box>
            )
          )
        })}
    </>
  )
}
